import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useHealth, useNetworkServices } from '../hooks/useAgent.js';
import { useAuth } from '../context/AuthContext.jsx';
import { summarizeNetworkServices, showNetworkServicesTile } from '../lib/networkServices.js';
import { useAlertsList } from '../hooks/useAlertsApi.js';
import HealthGauge from '../components/charts/HealthGauge.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';

import RelativeTime from '../components/shared/RelativeTime.jsx';
import { statusDot } from '../lib/statusColor.js';
import { CardSkeleton } from '../components/shared/Skeleton.jsx';
import { isStanding, STANDING_LABEL, STANDING_AFTER_DAYS } from '../lib/alertAge.js';

// The server's page ceiling. Counting needs every active alert; past this the
// page is a floor and the cards say so rather than undercount (#998 R3).
const COUNT_LIMIT = 500;
const ACTIVE_FILTERS = { state: 'active', severity: null, agent_id: null, device_id: null };

function countBySeverity(alerts, severity, now) {
  const matching = alerts.filter((a) => a.severity === severity);
  const standing = matching.filter((a) => isStanding(a, now)).length;
  return { fresh: matching.length - standing, standing };
}

export default function Overview() {
  const { data: health, isLoading: healthLoading } = useHealth();
  // S11-8: org-scoped D1 alerts rather than the agent-scoped proxy
  // to on-prem. Under the hood this hits /api/v1/alerts and is
  // invalidated by AppShell on alert.triggered / alert.resolved WS
  // events — so the summary card and recent list both stay live.
  const { data: alertPage, isLoading: alertsLoading } = useAlertsList(ACTIVE_FILTERS, { limit: COUNT_LIMIT, withMeta: true });
  const activeAlerts = alertPage?.items ?? [];
  const truncated = alertPage?.truncated ?? false;
  const { data: agents } = useQuery({
    queryKey: ['agents-list'],
    queryFn: () => api.get('/agents'),
    refetchInterval: 30_000,
  });

  if (healthLoading && alertsLoading) {
    return <CardSkeleton count={4} message="Loading overview…" />;
  }

  const overallScore = health?.overall_score ?? null;
  // Largest population first: a 1-device tower reading 0 should not lead the
  // row ahead of a 480-device segment reading 95 (vista#788). The gauge already
  // marks a small sample with n=.
  const segments = (Array.isArray(health?.segment_scores) ? [...health.segment_scores] : [])
    .sort((a, b) => (b.scored_devices ?? 0) - (a.scored_devices ?? 0));
  const collectors = Array.isArray(health?.collectors) ? health.collectors : [];

  const now = Date.now();
  const critical = countBySeverity(activeAlerts, 'critical', now);
  const warning = countBySeverity(activeAlerts, 'warning', now);
  const info = countBySeverity(activeAlerts, 'info', now);
  // Active alerts whose rule_type starts with `syslog_` — the count is
  // alert-state, not syslog-event volume. Pre-rename this card was
  // labeled "Syslog (24h)", which suggested the latter and read as
  // mysteriously-zero on healthy networks (4k+ events/hr ingested,
  // zero firing alerts → card said 0, operator distrusted the
  // dashboard). vista#275 renamed the card label to match the math;
  // a real event-volume card is tracked separately.
  const syslogCount = activeAlerts.filter((a) => typeof a.rule_type === 'string' && a.rule_type.startsWith('syslog')).length;

  const agentList = Array.isArray(agents) ? agents : [];
  const connectedCount = agentList.filter((a) => a.status === 'active').length;
  const totalAgents = agentList.length;

  // New before standing, each keeping the list's own order, so a backlog of
  // month-old alerts cannot fill all five rows ahead of today's (vista#788).
  const recentAlerts = [
    ...activeAlerts.filter((a) => !isStanding(a, now)),
    ...activeAlerts.filter((a) => isStanding(a, now)),
  ].slice(0, 5);

  // Partial coverage is amber, not green: "3 of 5 connected" painted green
  // reads as fine when two agents are dark, which is exactly the state an
  // operator needs to notice.
  const allConnected = totalAgents > 0 && connectedCount === totalAgents;
  const someConnected = connectedCount > 0 && connectedCount < totalAgents;
  const fleetStatus = allConnected ? 'healthy' : someConnected ? 'degraded' : 'unknown';

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Overview</h1>

      {/* Fleet agent coverage.
          vista#792 item 5: this and the header's "● Live" badge both read as
          "connected", and with a single agent they look redundant. They are
          different facts, so they are differentiated rather than merged —
          removing this one would lose the fleet count as soon as there is more
          than one agent:

            · header badge  — the SELECTED agent's live stream (WS push
              flowing = "Live", heartbeat only = "Online")
            · this banner   — how many of the org's agents are connected at all

          The count now always carries its denominator ("1 of 1 connected"),
          which says more than "1 Agent Connected" plus a separate
          "(2 disconnected)" suffix and cannot be misread as fleet-wide health. */}
      <div className={`flex items-center gap-3 rounded-lg border p-4 ${allConnected ? 'border-green-200 bg-green-50' : someConnected ? 'border-yellow-200 bg-yellow-50' : 'border-gray-200 bg-gray-50'}`}>
        <span className={`inline-block h-3 w-3 rounded-full ${statusDot(fleetStatus)}`} />
        <span className="text-sm font-medium text-gray-800">
          {totalAgents === 0
            ? 'No agents registered'
            : `Agents: ${connectedCount} of ${totalAgents} connected`}
        </span>
        <span className="text-xs text-gray-500">
          {totalAgents === 0
            ? 'Register an agent in Settings → Agents to get started.'
            : 'Fleet coverage — the header badge shows the selected agent’s live stream.'}
        </span>
      </div>

      {/* Health + Alert Summary */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
        <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-6">
          <HealthGauge score={overallScore} label="Network Health" />
        </div>

        <AlertCard label="Critical" count={critical.fresh} standing={critical.standing} truncated={truncated} color="red" filter="critical" />
        <AlertCard label="Warning" count={warning.fresh} standing={warning.standing} truncated={truncated} color="yellow" filter="warning" />
        <AlertCard label="Info" count={info.fresh} standing={info.standing} truncated={truncated} color="blue" filter="info" />
        <AlertCard label="Syslog Alerts" count={syslogCount} truncated={truncated} color="indigo" />
      </div>

      {truncated && (
        <p className="-mt-3 text-xs text-gray-500" role="note">
          {`More than ${COUNT_LIMIT} active alerts — the counts above cover the ${COUNT_LIMIT} highest-severity, newest ones and are a floor (marked +). `}
          <Link to="/alerts" className="font-medium text-blue-600 hover:text-blue-800">See them all →</Link>
        </p>
      )}

      {/* Segment Health */}
      {segments.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-800">Segment Health</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {segments.map((seg) => (
              <div key={seg.name} className="flex items-center gap-3">
                <HealthGauge score={seg.score} size="sm" sampleSize={seg.scored_devices} />
                <span className="text-sm font-medium text-gray-700">{seg.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Network Services (DNS / DHCP) — vista#607 */}
      <NetworkServicesCard />

      {/* Recent Alerts — last 5 active across all devices; row click → /alerts */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">Recent Alerts</h2>
          <Link to="/alerts" className="text-sm font-medium text-blue-600 hover:text-blue-800">
            View all →
          </Link>
        </div>
        {recentAlerts.length === 0 ? (
          <p className="text-sm text-gray-400">No active alerts</p>
        ) : (
          <div className="space-y-2">
            {recentAlerts.map((alert) => (
              <div
                key={alert.alert_id}
                className="flex w-full items-center gap-3 rounded-md border border-gray-100 px-3 py-2 text-left hover:border-gray-200 hover:bg-gray-50"
              >
                <StatusBadge status={alert.severity} />
                {/* Device name → device detail; the message → the alerts
                    list. Separate links avoid nesting interactive elements. */}
                {alert.device_id && (alert.device_name || '').trim() && (
                  <Link
                    to={`/devices/${alert.device_id}`}
                    className="whitespace-nowrap text-sm font-medium text-blue-700 hover:underline"
                  >
                    {alert.device_name}
                  </Link>
                )}
                <Link to="/alerts" className="flex-1 truncate text-sm text-gray-700">
                  {alert.message}
                </Link>
                <RelativeTime ts={alert.triggered_at} className="text-xs text-gray-400 whitespace-nowrap" fallback="" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Collector Status */}
      {collectors.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-800">Collectors</h2>
          <div className="flex flex-wrap gap-3">
            {collectors.map((col) => (
              <StatusBadge key={col.name} status={col.status} label={col.name} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// vista#607: at-a-glance status of the monitored network services (DNS/DHCP).
// One badge per kind carrying the worst health_state across that kind, with a
// click-through to the full Services view. Hidden when none are configured.
function NetworkServicesCard() {
  const { networkServicesEnabled } = useAuth();
  const { data } = useNetworkServices();
  const summary = summarizeNetworkServices(Array.isArray(data) ? data : []);
  // #610 review: gate on the per-org flag (same as the Services nav), not just
  // data-presence — a flag-off org with residual rows shouldn't show the tile.
  if (!showNetworkServicesTile(networkServicesEnabled, summary.length)) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Network Services</h2>
        <Link to="/services" className="text-sm font-medium text-blue-600 hover:text-blue-800">
          View all →
        </Link>
      </div>
      <div className="flex flex-wrap gap-3">
        {summary.map((s) => (
          <StatusBadge
            key={s.kind}
            status={s.state}
            label={s.count > 1 ? `${s.label} (${s.count})` : s.label}
          />
        ))}
      </div>
      {summary.some((s) => s.error) && (
        <div className="mt-3 space-y-1">
          {summary.filter((s) => s.error).map((s) => (
            <div key={s.kind} className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
              {s.label}: {s.error}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// AlertCard links to the Alerts list pre-filtered by severity so a
// user can jump from the summary straight into the relevant rows.
// Cards without a filter (Syslog) stay non-interactive.
//
// `count` is the alerts raised in the last week; `standing` the older ones. A
// card with nothing new keeps its number but drops its colour — the backlog is
// real, and it is not an emergency (UI_GUIDELINES §3.12).
function AlertCard({ label, count, color, filter, standing = 0, truncated = false }) {
  const colors = {
    red: 'border-red-200 bg-red-50 text-red-700',
    yellow: 'border-yellow-200 bg-yellow-50 text-yellow-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    indigo: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  };
  const tone = count === 0 && standing > 0 ? 'border-gray-200 bg-gray-50 text-gray-600' : colors[color];
  const inner = (
    <>
      <div className="text-3xl font-bold">{truncated ? `${count}+` : count}</div>
      <div className="text-sm font-medium opacity-75">{label}</div>
      {standing > 0 && (
        <div className="mt-1 text-xs opacity-75" title={`Active, raised more than ${STANDING_AFTER_DAYS} days ago`}>
          {`+${standing}${truncated ? '+' : ''} ${STANDING_LABEL}`}
        </div>
      )}
    </>
  );
  if (filter) {
    return (
      <Link
        to={`/alerts?severity=${filter}`}
        className={`block rounded-lg border p-6 transition-colors hover:shadow-sm ${tone}`}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className={`rounded-lg border p-6 ${tone}`}>
      {inner}
    </div>
  );
}
